import * as yup from 'yup';
import { CampaignConfigSchema, CampaignFormValues, FormMessage } from '../../types';
import { isFieldRequired } from '../../helpers';
import { validateMessageOrder } from './messageSchedule';

/** Field rules shared by the policies. Opt in per policy. */
interface CommonSchemaOptions {
  /** Require at least one scheduled message. */
  messages?: boolean;
  /** Require at least one recipient group when the config marks recipients required. */
  groups?: boolean;
  /** Require at least one keyword when the config marks keywords required. */
  keywords?: boolean;
}

export function commonSchemaShape(
  config: CampaignConfigSchema,
  options: CommonSchemaOptions = {},
): Record<string, yup.AnySchema> {
  const shape: Record<string, yup.AnySchema> = {
    name: yup.string().required('Name is required'),
  };

  if (options.messages) {
    shape.scheduledMessages = yup.array().min(1, 'At least one message is required');
  }

  if (options.groups && isFieldRequired(config.recipients)) {
    shape.groupIds = yup
      .array()
      .of(yup.string())
      .min(1, 'At least one recipient group is required');
  }

  if (options.keywords && isFieldRequired(config.keywords)) {
    shape.keywordIds = yup
      .array()
      .of(yup.string())
      .min(1, 'At least one keyword is required');
  }

  return shape;
}

/** Each send time must come after the previous one. */
export function withMessageOrderTest<T extends yup.AnyObjectSchema>(schema: T): T {
  return schema.test(
    'message-order',
    'Messages must be in chronological order',
    function (values) {
      const msgs = (values as CampaignFormValues).scheduledMessages;
      const startAt = (values as CampaignFormValues).dateScheduled;
      if (!msgs || msgs.length === 0) return true;
      return validateMessageOrder(msgs as FormMessage[], startAt).valid;
    },
  ) as T;
}
